// skipcq: JS-W1028
import { useCallback, useEffect, useState } from 'react';
import {
  listProfileProcessingFailures,
  resolveProfileProcessingFailure,
  retryProfileProcessingFailure,
} from '../services/beerProfileProcessingFailuresService';
import {
  PROFILE_PROCESSING_FILTER_ALL,
} from '../utils/brewery/beerProfileProcessingFailures';
import {
  captureProfileProcessingFailuresEmpty,
  captureProfileProcessingFailuresError,
  captureProfileProcessingFailuresResolve,
  captureProfileProcessingFailuresRetry,
  captureProfileProcessingFailuresView,
} from '../utils/beerIngestionAnalytics';

const EMPTY_SUMMARY = {
  open: 0,
  resolved: 0,
  byCategory: {},
  byStage: {},
  reportingAsOf: null,
  reportingStale: false,
};

/**
 * @param {{
 *   enabled?: boolean,
 *   disposition?: 'open'|'resolved',
 *   category?: string,
 *   failureStage?: string,
 * }} [options]
 */
export default function useBeerProfileProcessingFailures(options = {}) {
  const enabled = options.enabled !== false;
  const disposition = options.disposition === 'resolved' ? 'resolved' : 'open';
  const category = options.category && options.category !== PROFILE_PROCESSING_FILTER_ALL
    ? options.category
    : undefined;
  const failureStage = options.failureStage
    && options.failureStage !== PROFILE_PROCESSING_FILTER_ALL
    ? options.failureStage
    : undefined;

  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(EMPTY_SUMMARY);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [pendingItemId, setPendingItemId] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);

  const refresh = useCallback(() => {
    setReloadToken((token) => token + 1);
  }, []);

  useEffect(() => {
    if (!enabled) {
      setRows([]);
      setSummary(EMPTY_SUMMARY);
      setLoading(false);
      setError(null);
      return undefined;
    }
    let cancelled = false;
    const loadRows = async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await listProfileProcessingFailures({
          disposition,
          category,
          failureStage,
        });
        if (cancelled) {
          return;
        }
        setRows(result.rows);
        setSummary(result.summary);
        captureProfileProcessingFailuresView(result.summary?.open ?? 0);
        if (result.rows.length === 0) {
          captureProfileProcessingFailuresEmpty();
        }
      } catch (loadError) {
        if (cancelled) {
          return;
        }
        captureProfileProcessingFailuresError();
        setError(loadError?.message || 'Failed to load profile processing failures');
        setRows([]);
        setSummary(EMPTY_SUMMARY);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    loadRows();
    return () => {
      cancelled = true;
    };
  }, [enabled, disposition, category, failureStage, reloadToken]);

  const resolveFailure = useCallback(async (itemId) => {
    setActionLoading(true);
    setActionError(null);
    setPendingItemId(itemId);
    try {
      await resolveProfileProcessingFailure(itemId);
      captureProfileProcessingFailuresResolve(itemId);
      refresh();
    } catch (resolveError) {
      setActionError(resolveError?.message || 'Failed to resolve profile processing failure');
      throw resolveError;
    } finally {
      setActionLoading(false);
      setPendingItemId(null);
    }
  }, [refresh]);

  const retryFailure = useCallback(async (item) => {
    if (!item?.itemId) {
      return null;
    }
    setActionLoading(true);
    setActionError(null);
    setPendingItemId(item.itemId);
    try {
      const result = await retryProfileProcessingFailure(item.itemId);
      captureProfileProcessingFailuresRetry(
        item.itemId,
        item.category,
        item.breweryId,
        item.beerId,
      );
      refresh();
      return result;
    } catch (retryError) {
      setActionError(retryError?.message || 'Failed to retry profile processing failure');
      throw retryError;
    } finally {
      setActionLoading(false);
      setPendingItemId(null);
    }
  }, [refresh]);

  return {
    rows,
    summary,
    loading,
    error,
    actionError,
    actionLoading,
    pendingItemId,
    refresh,
    resolveFailure,
    retryFailure,
  };
}
